/**
 * World languages — same pattern as countries/cities:
 * - Prefer remote REST Countries (official languages worldwide)
 * - Cache in memory for 24h (no DB, no backend round-trip)
 * - Fall back to static ISO list when the API is unreachable
 */

import { WORLD_LANGUAGES } from 'data/worldLanguages';

const LANGUAGES_TTL_MS = 24 * 60 * 60 * 1000;
const REST_COUNTRIES_LANGUAGES_URL =
    'https://restcountries.com/v3.1/all?fields=languages';

let languagesCache = null; // { expiresAt, data: string[] }
let languagesInflight = null;

const uniqueSorted = (names) =>
    [...new Set((names || []).map((n) => String(n || '').trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
    );

const extractFromRestCountries = (payload) => {
    const names = [];
    (Array.isArray(payload) ? payload : []).forEach((country) => {
        const languages = country?.languages;
        if (!languages || typeof languages !== 'object') return;
        Object.values(languages).forEach((name) => {
            if (name) names.push(name);
        });
    });
    return uniqueSorted(names);
};

/** Merge API + static so offline fallback languages are never lost. */
const mergeWithStatic = (remoteNames) =>
    uniqueSorted([...(remoteNames || []), ...WORLD_LANGUAGES]);

export const invalidateLanguagesCache = () => {
    languagesCache = null;
    languagesInflight = null;
};

/**
 * Returns a sorted list of language display names.
 * Never touches PearlCRM DB — only browser memory + optional public API.
 */
export const fetchAllLanguages = async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) {
        invalidateLanguagesCache();
    }

    if (languagesCache && Date.now() <= languagesCache.expiresAt) {
        return languagesCache.data;
    }

    if (languagesInflight) {
        return languagesInflight;
    }

    languagesInflight = (async () => {
        try {
            const response = await fetch(REST_COUNTRIES_LANGUAGES_URL);
            if (!response.ok) {
                throw new Error(`REST Countries languages HTTP ${response.status}`);
            }
            const payload = await response.json();
            const remote = extractFromRestCountries(payload);
            const data = remote.length > 0 ? mergeWithStatic(remote) : [...WORLD_LANGUAGES];
            languagesCache = {
                expiresAt: Date.now() + LANGUAGES_TTL_MS,
                data,
            };
            return data;
        } catch (error) {
            console.error('Error fetching world languages:', error);
            // Cache the static fallback briefly so we don't hammer a down API.
            const data = [...WORLD_LANGUAGES];
            languagesCache = {
                expiresAt: Date.now() + 5 * 60 * 1000,
                data,
            };
            return data;
        } finally {
            languagesInflight = null;
        }
    })();

    return languagesInflight;
};

export const getStaticLanguages = () => [...WORLD_LANGUAGES];
